// frontend/src/lib/mfa.js
//
// Pure helpers for the login second factor (two-step verification). No React,
// no network: the pages in src/pages/LoginMfaStep.jsx and
// src/pages/SettingsSecurity.jsx call these, and src/lib/mfa.test.js pins them.
//
// The code shapes mirror backend/app/mfa.py: a TOTP code is 6 digits; a
// recovery code is 16 base32 characters (A to Z, 2 to 7), shown as four groups
// of four and accepted with or without the dashes, spaces and case.

export const TOTP_LENGTH = 6;
export const RECOVERY_LENGTH = 16;
export const RECOVERY_CODE_COUNT = 10;
export const LOW_RECOVERY_CODES = 3;

// The server's challenge lives 5 minutes. The page gives up 15 seconds early so
// a user is told to start over instead of being told their code was wrong.
export const CHALLENGE_TTL_MS = 5 * 60 * 1000;
export const CHALLENGE_MARGIN_MS = 15 * 1000;

const BASE32 = /^[A-Z2-7]+$/;

/** Letters and digits only, upper case: what the server compares. */
export function normalizeCode(input) {
  return String(input ?? '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();
}

/** 'totp', 'recovery' or null for anything else. */
export function codeKind(input) {
  const code = normalizeCode(input);
  if (code.length === TOTP_LENGTH && /^\d+$/.test(code)) return 'totp';
  if (code.length === RECOVERY_LENGTH && BASE32.test(code)) return 'recovery';
  return null;
}

/** True when `input` is a complete code of the kind `mode` asks for ('totp', 'recovery' or 'any'). */
export function isCompleteCode(input, mode = 'any') {
  const kind = codeKind(input);
  if (kind === null) return false;
  return mode === 'any' || kind === mode;
}

/** What the 6-digit field keeps from a keystroke or a paste: digits only, at most six. */
export function sanitizeTotpInput(raw) {
  return String(raw ?? '').replace(/\D/g, '').slice(0, TOTP_LENGTH);
}

/** What the recovery-code field keeps: base32 characters and the dash, upper case, bounded. */
export function sanitizeRecoveryInput(raw) {
  return String(raw ?? '').toUpperCase().replace(/[^A-Z2-7-\s]/g, '').slice(0, 24);
}

/**
 * For a field that takes either kind (the turn-off and new-codes dialogs):
 * letters, every digit, the dash and spaces, upper case, bounded. Not the
 * recovery sanitizer, which drops 0, 1, 8 and 9 and would mangle an app code.
 */
export function sanitizeAnyCodeInput(raw) {
  return String(raw ?? '').toUpperCase().replace(/[^A-Z0-9-\s]/g, '').slice(0, 24);
}

/**
 * Decide what a 200 from POST /auth/login means.
 *   { kind: 'session' }               a token: store it and go in
 *   { kind: 'mfa', challenge }        a second step is needed
 *   { kind: 'invalid' }               neither: never store anything
 * The last case is the guard against saving `undefined` as the token.
 */
export function classifyLoginResponse(data) {
  if (data && data.mfa_required === true && typeof data.challenge === 'string' && data.challenge) {
    return { kind: 'mfa', challenge: data.challenge };
  }
  if (data && typeof data.token === 'string' && data.token) {
    return { kind: 'session' };
  }
  return { kind: 'invalid' };
}

/**
 * Store a signed-in session the way the app has always read it. Returns true
 * when the stored copy reads back intact (the "session save failed" check).
 */
export function saveSession(data, storage) {
  storage.setItem('auth_token', data.token);
  storage.setItem('user_email', data.email);
  storage.setItem('user_roles', JSON.stringify(data.roles || []));
  return storage.getItem('auth_token') === data.token;
}

/** Where to land after the second step: the Security page when a recovery code was spent. */
export function afterSignInPath(body) {
  return body && body.recovery_code_used ? '/settings/security' : '/';
}

/** True once a challenge issued at `issuedAt` (ms) is too old to try. */
export function challengeExpired(issuedAt, now = Date.now()) {
  return now - issuedAt >= CHALLENGE_TTL_MS - CHALLENGE_MARGIN_MS;
}

const DEFAULT_MESSAGES = {
  401: 'That code did not work. Check your authenticator and try again.',
  403: 'This account cannot sign in right now. Contact your administrator.',
  429: 'Too many attempts. Wait a few minutes and try again.',
};

/** The message to show for a failed MFA call, preferring the server's own words. */
export function mfaErrorMessage(status, detail) {
  if (typeof detail === 'string' && detail.trim() && [400, 401, 403, 409, 429].includes(status)) {
    return detail;
  }
  return DEFAULT_MESSAGES[status] || 'Something went wrong on our side. Try again in a moment.';
}

/** 'on', 'pending' or 'off' from GET /auth/mfa/status. */
export function mfaState(status) {
  if (status && status.enabled) return 'on';
  if (status && status.pending) return 'pending';
  return 'off';
}

export function lowOnRecoveryCodes(remaining) {
  return Number.isFinite(remaining) && remaining <= LOW_RECOVERY_CODES;
}

/** Contents of the downloadable recovery codes file. */
export function recoveryCodesText(codes, account, when = new Date()) {
  const lines = [
    'Connex recovery codes',
    `Account: ${account}`,
    `Created: ${when.toISOString().slice(0, 10)}`,
    '',
    'Each code works once. Use one when you cannot reach your authenticator app.',
    'Keep this file somewhere safe and private, such as a password manager.',
    '',
    ...codes,
    '',
  ];
  return lines.join('\n');
}

/** Split a grouped setup key for display, keeping the server's grouping when present. */
export function setupKeyGroups(secretDisplay) {
  return String(secretDisplay ?? '').split(/\s+/).filter(Boolean);
}
